/**
 * TIME PROJECTION — price read as a count of days.
 *
 * From one clicked bar, the close's own digits become calendar-day offsets:
 *
 *   close 512.34  ->  digits "51234"  ->  n1 "51" = 51d   n2 "512" = 512d   n3 "5123" = 5123d
 *   close  48     ->  digits "48"     ->  n1 "48" = 48d   n2 "480" = 480d   n3 "4800" = 4800d
 *   close   9     ->  digits "9"      ->  n1 "90" = 90d   n2 "900" = 900d   n3 "9000" = 9000d
 *
 * <h2>ONE RULE, NOT THREE</h2>
 *
 * The brief states each offset separately with its own padding example. They are the same rule at
 * three lengths — take the first k digits, right-pad with {@code '0'} to length k — and that single
 * form reproduces every example it gives, including {@code 9 -> "90"} and {@code "48" -> "4800"}.
 * Written as three cases it would be three places for a pad to be wrong, and wrong only at one
 * length, which is the defect that ships.
 *
 * <h2>⚠ THE DIGITS ARE TAKEN LITERALLY, so a sub-penny price yields NOTHING</h2>
 *
 * "Strip the decimal point" on {@code 0.0001} gives {@code "00001"}, whose first two digits are
 * {@code "00"} — an offset of zero days, which is the anchor bar itself and not a projection. All
 * three offsets collapse that way, so a stock trading at a hundredth of a cent produces no
 * markers. That is the honest outcome and it is a real case in this store: HPIL last traded at
 * 0.0001 and carries thousands of all-zero rows. Stripping leading zeros instead would invent a
 * harmonic ({@code "1" -> 10 days}) out of a price that has no significant figures to read.
 *
 * <h2>⚠ AND AN EXPONENT STRING IS REFUSED</h2>
 *
 * {@code String(1e-7)} is {@code "1e-7"}, and this store genuinely holds values like
 * {@code 9.9999997E-5} — scientific notation is deliberately accepted by the EOD writer. Removing
 * the point from {@code "1e-7"} gives {@code "1e-7"}, whose first two characters are {@code "1e"}:
 * {@code Number("1e")} is NaN, and a silently NaN offset would drop the marker for a reason nobody
 * could find. Refused explicitly instead.
 *
 * <h2>CALENDAR days, then snapped to a column</h2>
 *
 * The offsets are calendar days — the Gann reading, and what the brief's "+51d" names. A calendar
 * date is then resolved to the nearest axis column AT OR AFTER it, because a projected day is very
 * often a weekend and a category axis has no column for one. The same two-step every date-driven
 * study here uses, and a snapped marker says so on its card rather than silently claiming the
 * Monday was the projection.
 */

import { isoDay } from './getChartFutureCeilingDate';
import { indexAtOrAfter } from './timeBandOverlay';
import {
  pickForTheme, projectionOpacity, PROJECTION_WIDTH,
  projectionLabelChip, PROJECTION_CHIP_PADDING,
} from './overlayInk';

/** Every overlay this tool creates is prefixed, so toggling can find and remove them. */
export const TIMEPROJ_PREFIX = 'timeproj-';

/**
 * The marker overlay's id.
 *
 * <p>⚠ ITS SERIES ID MUST BE IN {@code TOOLTIP_ALLOWED_PREFIXES}. The renderer names a
 * pivot-marker series {@code pivot-markers-<overlay.id>}, and {@code restrictTooltips} runs LAST
 * and strips {@code tooltip} from any series not on that list — so {@code researchTooltip} is
 * necessary and not sufficient. Six studies have shipped drawing hover-dead marks on exactly this.
 */
export const TIMEPROJ_MARKERS_ID = `${TIMEPROJ_PREFIX}markers`;

/**
 * ⚠ INDIGO, chosen for HUE separation as much as for contrast — and now a THEME PAIR.
 *
 * <p>Contrast did not decide the hue. Teal, magenta and blue-grey all clear the 3:1 a graphical
 * object needs; what decided it is that every other hue on this chart is taken: PTA amber, PTB
 * cyan (so teal is out — same family), CTS green/red/amber, the analog scanner pink, the midpoint
 * trine #AB47BC, and SK360's own goldenrod #B8860B, which these marks routinely sit beside.
 *
 * <p>⚠ WHY IT BECAME A PAIR. `#5C6BC0` measures 4.66:1 on light and 3.89:1 on dark — both clear
 * the floor, so this was never illegible. It was reported as FAINT on dark, which 3.89 against a
 * brighter neighbour is: the goldenrod beside it sits at 5.82 on the same canvas.
 *
 * <p>So the dark value moves up the SAME indigo family rather than to a new hue — `#9FA8DA`, at
 * 8.19:1 on dark. That keeps the survey above valid: a family shift would reopen the one question
 * it settled.
 */
export const TIMEPROJ_COLORS = Object.freeze({ dark: '#9FA8DA', light: '#5C6BC0' });

/** The colour for the active theme. */
export const timeProjColor = (mode) => pickForTheme(TIMEPROJ_COLORS, mode);

/** The three digit lengths, in the order the brief names them: n1, n2, n3. */
export const TIMEPROJ_DIGIT_LENGTHS = Object.freeze([2, 3, 4]);

/** Offset names, so a card and a status line cannot disagree about which is which. */
export const TIMEPROJ_OFFSET_NAMES = Object.freeze(['n1', 'n2', 'n3']);

/**
 * The close with its decimal point removed, or null when there are no digits to read.
 *
 * @param {number} close
 * @returns {string|null}
 */
export function priceDigits(close) {
  const c = Math.abs(Number(close));
  if (!Number.isFinite(c) || c === 0) return null;
  const s = String(c);
  // See the header note: an exponent string has no digit harmonic and would parse to NaN.
  if (s.indexOf('e') >= 0 || s.indexOf('E') >= 0) return null;
  return s.replace('.', '');
}

/**
 * The three day offsets for one close.
 *
 * @returns {Array<{name:string, length:number, text:string, days:number}>} empty when unreadable
 */
export function offsetsFrom(close) {
  const digits = priceDigits(close);
  if (!digits) return [];
  return TIMEPROJ_DIGIT_LENGTHS.map((length, i) => {
    const text = digits.slice(0, length).padEnd(length, '0');
    return { name: TIMEPROJ_OFFSET_NAMES[i], length, text, days: Number(text) };
  });
}

/**
 * {@code days} calendar days after an ISO day, as an ISO day.
 *
 * <p>⚠ UTC THROUGHOUT. {@code new Date(y, m, d)} is local, so west of UTC it lands on the previous
 * day and two readers of one chart would see markers a column apart. The rule this codebase states
 * for every date mapping.
 */
export function addCalendarDays(day, days) {
  const base = isoDay(day);
  const n = Number(days);
  if (!base || !Number.isFinite(n)) return null;
  const t = Date.parse(`${base}T00:00:00Z`);
  if (!Number.isFinite(t)) return null;
  return new Date(t + Math.trunc(n) * 86400000).toISOString().slice(0, 10);
}

/**
 * Each offset resolved against the axis, with its fate recorded.
 *
 * <p>Nothing is silently discarded: a dropped offset carries the reason it was dropped, so the
 * caller can say "two drawn, one beyond the chart" rather than showing fewer markers than the
 * arithmetic produced and leaving a reader to wonder which.
 *
 * @param {string[]} axisDates the padded axis
 * @param {string}   anchorDate the clicked bar's ISO day
 * @param {Array}    offsets    from {@link offsetsFrom}
 */
export function resolveOffsets(axisDates, anchorDate, offsets) {
  const axis = Array.isArray(axisDates) ? axisDates : [];
  const anchor = isoDay(anchorDate);
  if (!anchor || axis.length === 0) return [];

  return (offsets || []).map((o) => {
    // Zero is the anchor bar, and a negative offset cannot arise from digits — both mean there is
    // no projection to make rather than one that failed.
    if (!o || !Number.isFinite(o.days) || o.days <= 0) {
      return { ...o, date: null, index: -1, drawn: false, reason: 'no-offset' };
    }
    const date = addCalendarDays(anchor, o.days);
    if (!date) return { ...o, date: null, index: -1, drawn: false, reason: 'unreadable' };

    // THE DISPLAY BOUNDARY CHECK. -1 means the axis — already capped at this account's own
    // forward projection ceiling — does not reach that far, so there is no column to draw on.
    const index = indexAtOrAfter(axis, date);
    if (index < 0) return { ...o, date, index: -1, drawn: false, reason: 'beyond-chart' };

    return {
      ...o,
      date,
      index,
      column: axis[index],
      // The projected day was not a trading column, so the marker sits on the next one.
      snapped: axis[index] !== date,
      drawn: true,
      reason: null,
    };
  });
}

/**
 * Builds the Time Projection overlays for one anchor bar.
 *
 * <p>Admin is re-checked here rather than only on the control: a hidden button is a convenience
 * and not a guard.
 *
 * <p>⚠ ONE MARKER PER COLUMN, naming every offset that landed on it. Two different calendar
 * offsets can snap to the same trading day — 51 and 52 days ahead both land on the Monday when the
 * 51st is a Saturday — and two marks stacked on one column would be one mark as far as the eye is
 * concerned while the card showed whichever was drawn last.
 *
 * <p>⚠ THE MARKS CARRY NO PRICE, which is what puts them on the rail. This study computes a DATE
 * and nothing else; {@code onRailFor} routes a point with no finite price to the pinned 0..1 axis
 * by rule, so the absence IS the instruction. A mark at a price would assert a level the study
 * never derived — the same reason the CTS marks were moved off the candles.
 *
 * @returns {{overlays:Array, summary:object}|null}
 */
export function buildTimeProjection(barData, {
  isAdmin = false, anchorIndex = null, extendedDates = null,
} = {}) {
  if (!isAdmin) return null;
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) return null;

  const { dates, ohlc } = barData;
  if (dates.length === 0) return null;

  const i = Number.isInteger(anchorIndex) ? anchorIndex : dates.length - 1;
  if (i < 0 || i >= dates.length) return null;

  const anchorDate = isoDay(dates[i]);
  // ⚠ [open, CLOSE, low, high]. Reading [3] would read every projection off the bar's high.
  const bar = ohlc[i];
  const close = Array.isArray(bar) ? Number(bar[1]) : NaN;
  if (!anchorDate || !Number.isFinite(close)) return null;

  const offsets = offsetsFrom(close);
  if (offsets.length === 0) return null;

  const axis = Array.isArray(extendedDates) && extendedDates.length > dates.length
    ? extendedDates
    : dates;

  const resolved = resolveOffsets(axis, anchorDate, offsets);
  const drawn = resolved.filter(r => r.drawn);

  // Grouped by column, so each marker names everything that landed there.
  const byColumn = new Map();
  drawn.forEach((r) => {
    if (!byColumn.has(r.index)) byColumn.set(r.index, []);
    byColumn.get(r.index).push(r);
  });

  const priceText = Math.abs(close) >= 1000 ? close.toFixed(0) : close.toFixed(2);

  const points = [...byColumn.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([index, group]) => {
      const names = group.map(g => `+${g.days}d`).join(', ');
      const lines = group.map(g => `${g.name}  +${String(g.days).padStart(4)}d  `
        + `first ${g.length} digits of "${priceDigits(close)}" -> "${g.text}"`);
      if (group.some(g => g.snapped)) {
        lines.push(`snapped forward from ${group.filter(g => g.snapped)
          .map(g => g.date).join(', ')} — not a trading day`);
      }
      return {
        date: axis[index],
        // ⚠ NO `price`, deliberately. See the note above: that is what puts it on the rail.
        label: group.map(g => g.name).join('/'),
        color: timeProjColor(),
        // The brief's own card line, minus the currency symbol: the same rules run over a US
        // equity, an Indian equity, a metal and a currency pair, so no one symbol is correct.
        title: `Time Projection ${names} (from ${priceText})`,
        tooltipLines: lines,
        tooltipFooter: `anchored on ${anchorDate}`,
      };
    });

  if (points.length === 0) {
    // Every offset was dropped. Returned as a summary with no overlays rather than null, so the
    // caller can say WHY — "all three land past the chart" is a finding, and a tool that appears
    // to do nothing is the thing this avoids.
    return { overlays: [], summary: summarise(i, anchorDate, close, resolved, 0) };
  }

  const overlays = [{
    type: 'pivotMarkers',
    id: TIMEPROJ_MARKERS_ID,
    label: 'Time Projection',
    // The brief's ⇓. A built-in ECharts symbol, so no custom path is needed.
    symbol: 'arrow',
    symbolSize: 11,
    color: timeProjColor(),
    /*
     * ⚠ THE CHIP TRAVELS WITH THE HUE.
     *
     * The renderer's label chip is a hardcoded white and the label's colour is this overlay's own
     * hue, so the text's contrast is against the CHIP rather than the canvas. Lifting the dark
     * value to `#9FA8DA` takes it to 8.19:1 on the dark canvas and 2.2:1 on a white chip — so
     * brightening the mark without bringing its chip would have made the badge unreadable at
     * exactly the moment the line became visible.
     */
    labelChipColor: projectionLabelChip(),
    labelChipPadding: PROJECTION_CHIP_PADDING,
    points,
    // At the foot of the grid — the study asserts a date and no price.
    anchor: 'bottom',
    /*
     * ⚠ A FAINT VERTICAL PER MARK, which is the one departure from the eclipse study's shape.
     *
     * That study marks up to 1,719 bars, so its rail reads as a band. This marks at most three,
     * and an 11px arrow every few hundred columns at the foot of a grid whose candles sit hundreds
     * of pixels above is not findable by scanning — the exact finding recorded for the CTS forward
     * marks, which were drawn correctly and unfindable for weeks.
     *
     * <p>⚠ Opacity is NOT set per vertical — the renderer reads it from
     * {@code overlay.verticalOpacity} and a per-vertical {@code opacity} key is silently ignored,
     * so passing one would read as a setting that does nothing. The WIDTH, by contrast, IS read
     * per vertical, which is why it is set there and the opacity is set on the overlay.
     *
     * <p>⚠ 0.3 WAS TOO FAINT AND THAT WAS THE COMPLAINT. The renderer's default suits a background
     * stripe a reader audits price against; this guide IS the finding. {@code projectionOpacity()}
     * is theme-aware for a measured reason — on a light canvas 0.7 puts a saturated line back
     * below the 3:1 floor. See overlayInk.js.
     */
    verticalOpacity: projectionOpacity(),
    verticals: points.map(p => ({
      date: p.date, color: timeProjColor(), width: PROJECTION_WIDTH,
    })),
    // Admin research reached from the Admin Panel, so there is no customer for the short-bar lock
    // to protect. This exempts the card from that clause; the whitelist is the other gate.
    researchTooltip: true,
    // The card hangs off a mark on the rail, where ECharts would let it fall off the bottom.
    confineTooltip: true,
  }];

  return { overlays, summary: summarise(i, anchorDate, close, resolved, points.length) };
}

function summarise(anchorIndex, anchorDate, close, resolved, markerCount) {
  return {
    anchorIndex,
    anchorDate,
    anchorClose: close,
    digits: priceDigits(close),
    offsets: resolved,
    markerCount,
    drawnCount: resolved.filter(r => r.drawn).length,
    beyondCount: resolved.filter(r => r.reason === 'beyond-chart').length,
    noOffsetCount: resolved.filter(r => r.reason === 'no-offset').length,
    snappedCount: resolved.filter(r => r.drawn && r.snapped).length,
  };
}

/** Whether the tool's overlays are currently on the chart. */
export function hasTimeProjectionOverlays(overlays) {
  return Array.isArray(overlays)
    && overlays.some(o => o && typeof o.id === 'string' && o.id.startsWith(TIMEPROJ_PREFIX));
}

/** The overlay list with this tool's overlays removed. */
export function withoutTimeProjectionOverlays(overlays) {
  if (!Array.isArray(overlays)) return [];
  return overlays.filter(o => !(o && typeof o.id === 'string'
    && o.id.startsWith(TIMEPROJ_PREFIX)));
}
